import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import produce from 'utils/produce';

import useDataContext from 'data/context/data';
import dataApi from 'data/api/data';
import { getNumberOfPages, getTOCPages } from 'editor-utils/template';
import assetsApi from '../api/assets';
import useEditorContext from './editorContext';

const EditorItemContext = createContext();

/**
 * Create new array with all products and templates.
 * @param {object} product
 * @param {object} template
 * @param {Array} productsIds
 * @param {Array} alternativeProducts
 * @param {Array} alternativeTemplates
 * @returns {Array}
 */
const mapProductsWithTemplates = (
  product,
  template,
  productsIds = [],
  alternativeProducts = [],
  alternativeTemplates = [],
) => {
  const versionedProduct = (alternativeProduct, alternativeTemplate) => {
    return {
      ...alternativeProduct,
      version: alternativeTemplate.version,
      templateRootPath: alternativeTemplate.assetRootUrl,
      templateUrl: alternativeTemplate.templateUrl,
      template: alternativeTemplate,
    };
  };

  if (productsIds.length > 0) {
    return productsIds.reduce((acc, productId) => {
      if (productId === product.id) {
        acc.push(versionedProduct(product, template));
      } else {
        const alternativeProduct = alternativeProducts.find(p => p.id === productId);
        const alternativeTemplate = alternativeTemplates.find(t => t.id === productId);
        if (alternativeProduct && alternativeTemplate) {
          acc.push(versionedProduct(alternativeProduct, alternativeTemplate));
        }
      }

      return acc;
    }, []);
  }

  return [versionedProduct(product, template)];
};

export const EditorItemContextProvider = props => {
  const { id, item, config, params } = props;

  const { platform, accessToken } = useEditorContext();
  const dataContext = useDataContext();

  const product = useMemo(() => {
    //
    // Update price when is bundle product
    //
    if (item.bundle) {
      const bundle = dataContext.getSkuProduct(item.bundle.id);
      if (bundle) {
        return produce(props.product, draft => {
          draft.price = bundle.price;
        });
      }
    }

    return props.product;
  }, [props.product]);

  const template = useMemo(() => props.template, [props.template]);

  const [alternatives, setAlternatives] = useState(mapProductsWithTemplates(product, template));
  useEffect(() => {
    const alternativeProducts = dataContext.getSkuProducts(
      config.alternatives.filter(alternativeId => alternativeId !== product.id),
    );

    if (alternativeProducts.length > 0) {
      (async () => {
        const { data: alternativeTemplates } = await dataApi.getVersionedTemplates(
          alternativeProducts.map(alternativeProduct => ({
            productId: alternativeProduct.id,
            version: template.version,
          })),
        );

        setAlternatives(
          mapProductsWithTemplates(
            product,
            template,
            config.alternatives,
            alternativeProducts,
            alternativeTemplates,
          ),
        );
      })();
    }
  }, []);

  const [assetCategories, setAssetCategories] = useState([]);
  useEffect(() => {
    (async () => {
      const { data, error } = await assetsApi(accessToken).getCategories();
      if (error) {
        console.log(error);
        return;
      }
      setAssetCategories(data);
    })();
  }, []);

  const numberOfPages = useMemo(() => {
    const value = getNumberOfPages(template, item.pages);
    return template.cover ? value - 1 : value;
  }, [template, item.pages]);

  const tocPageIds = useMemo(() => getTOCPages(template).map(page => page.id), [template]);

  const enabledTOC = useMemo(() => {
    return !!item.pages.find(page => tocPageIds.indexOf(page.id) >= 0);
  }, [tocPageIds, item.pages]);

  return (
    <EditorItemContext.Provider
      value={{
        platform,
        id,
        item,
        config,
        product,
        template,
        params,
        alternativeProducts: alternatives,
        numberOfPages,
        tocPageIds,
        enabledTOC,
        assetCategories,
      }}
    >
      {props.children}
    </EditorItemContext.Provider>
  );
};

EditorItemContextProvider.propTypes = {
  id: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
  params: PropTypes.array.isRequired,
  children: PropTypes.node,
};

const useEditorItemContext = () => {
  return useContext(EditorItemContext);
};

export default useEditorItemContext;
